/**
 * Header behaviour: sticky state, hide-on-scroll, and the mobile menu drawer.
 *
 * The drawer markup uses native <details>, so navigation already works without
 * this file. Everything here is enhancement: animation, focus trap and the
 * scroll lock.
 *
 * Lifecycle: every listener this element puts on `document` or `window` is
 * registered with the instance's AbortController signal, and
 * disconnectedCallback aborts it. Without that, each Theme Editor re-render of
 * the header would leave another scroll handler running against a detached
 * copy of the element.
 */
(function () {
  'use strict';

  const { a11y } = window.Theme;

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.isSticky = this.dataset.sticky === 'true';
      this.hidesOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastScrollY = window.scrollY;

      this.setupScroll();
      this.setupMenuDrawer();
      this.setupDesktopNav();
      this.publishHeight();
    }

    /**
     * Publishes the header height as --header-height on <html>.
     *
     * A sticky sidebar elsewhere on the page has to know how far down to
     * start, and the height depends on the logo, the padding settings and
     * whether an announcement bar is present — none of which Liquid can
     * resolve into a number.
     */
    publishHeight() {
      const write = () => {
        document.documentElement.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);
      };

      write();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(write);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', window.Theme.utils.debounce(write, 150), { signal: this.signal });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    /* ---------------- Scroll state ---------------- */

    setupScroll() {
      if (!this.isSticky && this.dataset.transparent !== 'true') return;

      const onScroll = window.Theme.utils.throttle(() => {
        const y = window.scrollY;

        this.dataset.scrolled = y > 8 ? 'true' : 'false';

        if (this.hidesOnScroll) {
          // Never hide while a drawer is open or near the top of the page.
          const shouldHide = y > this.offsetHeight && y > this.lastScrollY && !this.drawerOpen;
          this.dataset.hidden = shouldHide ? 'true' : 'false';
        }

        this.lastScrollY = y;
      }, 100);

      window.addEventListener('scroll', onScroll, { passive: true, signal: this.signal });
      onScroll();
    }

    /* ---------------- Mobile drawer ---------------- */

    setupMenuDrawer() {
      this.drawer = document.querySelector('[data-menu-drawer]');
      this.overlay = document.querySelector('[data-menu-overlay]');
      this.toggle = this.querySelector('[data-menu-toggle]');

      if (!this.drawer || !this.toggle) return;

      this.closeButton = this.drawer.querySelector('[data-menu-close]');

      this.toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.openDrawer();
      });

      this.closeButton?.addEventListener('click', () => this.closeDrawer());
      this.overlay?.addEventListener('click', () => this.closeDrawer());

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && this.drawerOpen) this.closeDrawer();
        },
        { signal: this.signal }
      );
    }

    openDrawer() {
      if (!this.drawer) return;

      this.drawer.hidden = false;
      this.overlay.hidden = false;

      // Force a layout pass so the transition has a starting point, then set
      // the state synchronously. requestAnimationFrame does not fire in a
      // background tab, which would leave the drawer open but invisible.
      void this.drawer.offsetWidth;
      this.drawer.dataset.open = 'true';
      this.overlay.dataset.open = 'true';

      this.drawerOpen = true;
      this.toggle.setAttribute('aria-expanded', 'true');
      a11y.lockScroll();
      a11y.trapFocus(this.drawer, this.closeButton || this.drawer);
    }

    closeDrawer() {
      if (!this.drawer || !this.drawerOpen) return;

      this.drawer.dataset.open = 'false';
      this.overlay.dataset.open = 'false';
      this.drawerOpen = false;
      this.toggle.setAttribute('aria-expanded', 'false');

      a11y.removeTrapFocus(true);
      a11y.unlockScroll();

      // Wait for the slide-out to finish before removing it from the a11y tree.
      const onDone = () => {
        if (this.drawer.dataset.open !== 'true') {
          this.drawer.hidden = true;
          this.overlay.hidden = true;
        }
      };
      this.drawer.addEventListener('transitionend', onDone, { once: true });
      setTimeout(onDone, 500);
    }

    /* ---------------- Desktop dropdowns ---------------- */

    setupDesktopNav() {
      const dropdowns = Array.from(this.querySelectorAll('[data-nav-details]'));
      if (!dropdowns.length) return;

      // Only one dropdown open at a time.
      dropdowns.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          dropdowns.forEach((other) => {
            if (other !== details) other.open = false;
          });
        });

        // Pointer users expect hover; keyboard users get click and Escape.
        details.addEventListener('mouseenter', () => {
          if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) details.open = true;
        });
        details.addEventListener('mouseleave', () => {
          if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) details.open = false;
        });
      });

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape') return;
          dropdowns.forEach((details) => {
            if (details.open) {
              details.open = false;
              details.querySelector('summary')?.focus();
            }
          });
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'click',
        (event) => {
          if (this.contains(event.target)) return;
          dropdowns.forEach((details) => {
            details.open = false;
          });
        },
        { signal: this.signal }
      );
    }
  }

  customElements.define('site-header', SiteHeader);
})();
